import React, { useEffect, useState } from 'react';
import { Avatar, Box, Paper, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Profile } from '@tassomutuo-backoffice/types';
import composeImageUri from '../../../../utils/composeImageUri';
import ProfileTableRenderCellAction from './ProfileTableRenderCellAction';
import { useQuery } from '@tanstack/react-query';
import { getIntranetUsers } from '../../../../services/api/profile/profile';
import FilterEmailInput from './FilterEmailInput';
import { CtaButton } from '@tassomutuo-backoffice/ui';

interface IProps {
  onEditRowClick: (row: Profile) => void;
  onCreateNewClick: () => void;
}

const defaultPageModel = {
  page: 0,
  pageSize: 10,
};

const IntranetUsersTable = ({ onEditRowClick, onCreateNewClick }: IProps) => {
  const [paginationModel, setPaginationModel] = useState(defaultPageModel);
  const [filterEmail, setFilterEmail] = useState('');

  const {
    isPending,
    data: intranetUsersData,
    // error,
  } = useQuery({
    queryKey: ['IntranetUsers', paginationModel, filterEmail],
    queryFn: () =>
      getIntranetUsers({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        email: filterEmail,
      }),
  });

  const [rowCountState, setRowCountState] = React.useState(
    intranetUsersData?.totalResults || 0
  );

  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      intranetUsersData?.totalResults !== undefined
        ? intranetUsersData?.totalResults
        : prevRowCountState
    );
  }, [intranetUsersData?.totalResults, setRowCountState]);

  const onSubmitFilterEmail = (email: string) => {
    if (email) {
      setFilterEmail(email);
      setPaginationModel(defaultPageModel);
    } else {
      setFilterEmail('');
      setPaginationModel(defaultPageModel);
    }
  };

  const tableColumns: GridColDef<Profile>[] = [
    {
      field: 'profileImage',
      headerName: ' ',
      renderCell: ({ row }) => (
        <Avatar
          src={composeImageUri(row?.tassomutuoProfile?.profileImageUrl)}
        />
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'fullName',
      headerName: 'Nome',
      flex: 1,
      valueGetter: ({ row }) => `${row.name} ${row.lastName}`,
    },
    {
      field: 'provinces',
      headerName: 'Province',
      flex: 1,
      valueGetter: ({ row }) => row?.tassomutuoProfile?.provinces,
    },
    {
      headerName: ' ',
      field: ' ',
      flex: 0.5,
      sortable: false,
      renderCell: ({ row }) => (
        <ProfileTableRenderCellAction onEditClick={() => onEditRowClick(row)} />
      ),
      minWidth: 100,
    },
  ];

  return (
    <Box mt={8}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        mt={4}
        mb={2}
      >
        <Typography variant="h5">Utenti Intranet</Typography>
        <CtaButton variant="contained" onClick={onCreateNewClick}>
          Crea nuovo
        </CtaButton>
      </Box>
      <FilterEmailInput onSubmit={onSubmitFilterEmail} />
      <Paper>
        <DataGrid
          columns={tableColumns}
          rows={intranetUsersData?.results || []}
          disableRowSelectionOnClick
          autoHeight
          rowCount={rowCountState}
          loading={isPending}
          pageSizeOptions={[10]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
        />
      </Paper>
    </Box>
  );
};

export default IntranetUsersTable;
